import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import "./i18n";
import { ThemeProvider } from "./components/ThemeProvider";
import { initViewportFix } from "./utils/viewportFix";

initViewportFix();

// ── Sentry (only initializes if DSN is provided) ──────────────────────────────
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    environment: import.meta.env.MODE,
  });
}

// Without a boundary, any uncaught render error unmounts the whole app and
// leaves a blank screen. Show the error and offer a reload instead; Sentry
// captures it automatically when a DSN is configured.
function crashFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center text-ink">
      <div className="text-5xl select-none">🎲</div>
      <p className="text-ink/80 font-bold">Что-то пошло не так / Something went wrong</p>
      <p className="text-xs text-red-300/80 font-mono break-all max-w-sm">{message}</p>
      <button
        onClick={() => {
          resetError();
          window.location.reload();
        }}
        className="px-8 py-3 rounded-xl font-bold bg-blue-600 hover:bg-blue-500 text-white transition-colors active:scale-95"
      >
        Перезагрузить / Reload
      </button>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={crashFallback}>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
